import { format as d3Format } from "d3-format";

import { beforeAndAfterDecimal } from "../../utils/decimal";
import type { FormatOptions } from "./format.types";

const DEFAULT_PRECISION = 2;

/**
 * Compact-notation units (largest first), mirroring the lowercase `k`/`m`/`b`/`t`
 * suffixes the previous `numeral` `a` token produced. We render these manually
 * (rather than `Intl` `notation: "compact"`) to keep the exact casing and
 * 2-significant-decimal output the app has always shipped.
 */
const COMPACT_UNITS: readonly { threshold: number; suffix: string }[] = [
  { threshold: 1e12, suffix: "t" },
  { threshold: 1e9, suffix: "b" },
  { threshold: 1e6, suffix: "m" },
  { threshold: 1e3, suffix: "k" },
];

/** De-duplicates the failure warning so a bad value can't flood the console from a hot render loop. */
const warnedFailures = new Set<string>();

/**
 * Best-effort coercion to a finite JS number that **never throws**. Handles
 * plain numbers, bigints, numeric strings, and exotic runtime values whose
 * `Number()` coercion throws — most notably Arrow `Decimal` wrappers
 * (`DecimalBigNum`), which raise a `RangeError`. Returns `NaN` when the value
 * isn't numeric so callers can fall back gracefully.
 */
const toFiniteNumber = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (typeof value === "bigint") return Number(value);
  try {
    const n = Number(value);
    if (!Number.isNaN(n)) return n;
  } catch {
    // Some Arrow value wrappers throw on `Number()` — fall through to string parse.
  }
  return Number.parseFloat(String(value));
};

/**
 * Formats a finite number with grouping separators.
 *
 * - `long`: trims trailing zeros up to `precision` fraction digits.
 * - `short`: compact `k`/`m`/`b`/`t` notation with fixed `precision` fraction
 *   digits, then strips an all-zero fractional part (e.g. `4.00` -> `4`).
 */
const formatPlainNumber = (
  n: number,
  precision: number,
  isShort: boolean,
): string => {
  if (!isShort) {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: precision,
    }).format(n);
  }

  const abs = Math.abs(n);
  const unit = COMPACT_UNITS.find((u) => abs >= u.threshold);
  const scaled = unit ? n / unit.threshold : n;
  const fixed = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(scaled);
  // Strip a fractional part that is entirely zeros (e.g. `1.00k` -> `1k`).
  const trimmed = fixed.replace(/\.0+(?=\D|$)/, "");
  return `${trimmed}${unit?.suffix ?? ""}`;
};

export const formatNumber = (options: FormatOptions): string => {
  const { format: columnFormat, specifier, suffix } = options;
  const isShort = options.style === "short";

  // Per-format options (only present on the relevant variants).
  const explicitDecimals =
    columnFormat.kind === "NUMBER" || columnFormat.kind === "PERCENT"
      ? columnFormat.decimals
      : undefined;
  const percentScale =
    columnFormat.kind === "PERCENT" ? columnFormat.scale : undefined;
  const currencySymbol =
    columnFormat.kind === "CURRENCY" ? columnFormat.symbol : undefined;

  let result: string;

  try {
    const value = toFiniteNumber(options.value);

    if (Number.isNaN(value)) {
      result = String(options.value);
    } else if (specifier) {
      result = d3Format(specifier)(value);
    } else if (columnFormat.kind === "NUMBER_PLAIN") {
      result = String(value);
    } else {
      const [, after] = beforeAndAfterDecimal(value);
      const precision =
        explicitDecimals ??
        (isShort ? DEFAULT_PRECISION : Math.min(after, DEFAULT_PRECISION));

      if (columnFormat.kind === "PERCENT") {
        // `0-100` already holds whole percentages; `0-1` is a fraction we scale.
        const display = percentScale === "0-100" ? value : value * 100;
        result = `${formatPlainNumber(display, precision, isShort)}%`;
      } else if (columnFormat.kind === "CURRENCY") {
        const symbol = currencySymbol || "$";
        const body = formatPlainNumber(value, precision, isShort);
        // Keep the symbol after the minus sign for negatives (e.g. `-$10`).
        result = body.startsWith("-")
          ? `-${symbol}${body.slice(1)}`
          : `${symbol}${body}`;
      } else {
        result = formatPlainNumber(value, precision, isShort);
      }
    }
  } catch (err) {
    // Never swallow silently: a thrown formatter previously fell back to the
    // raw value, masking the real bug. Surface it (deduped) and degrade.
    const key = `${columnFormat.kind}:${err instanceof Error ? err.message : String(err)}`;
    if (!warnedFailures.has(key)) {
      warnedFailures.add(key);
      console.warn(
        `formatNumber failed for kind=${columnFormat.kind}; falling back to raw string`,
        err,
      );
    }
    result = String(options.value);
  }

  if (suffix && !isShort) {
    return `${result} ${suffix}`;
  }

  return result;
};
